import {
    Label
} from 'shared/components/ui/label';
import {
    Input
} from 'shared/components/ui/input';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Button
} from 'shared/components/ui/button';
import {
 JSX
} from 'react';

// Define types for the selectedItem and style
interface Style {
    color: string;
    size: string;
    font: string;
}

interface SelectedItem {
    name: string;
    description: string;
    type: 'table' | 'form' | 'chart' | 'custom'; // You can extend this list
    style: Style;
}

// Define types for the props
interface OpCodeConfigurationProps {
    selectedItem: SelectedItem;
    setSelectedItem: React.Dispatch<React.SetStateAction<SelectedItem>>;
    handleSaveChanges: () => void;
}

export default function OpCodeConfiguration({
    selectedItem, setSelectedItem, handleSaveChanges
}: OpCodeConfigurationProps): JSX.Element {

    const handleInputChange = (field: keyof SelectedItem, value: string) => {
        setSelectedItem({
            ...selectedItem,
            [field]: value
        });
    };

    // Handle style changes
    const handleStyleChange = (field: keyof Style, value: string) => {
        setSelectedItem({
            ...selectedItem,
            style: {
                ...selectedItem.style,
                [field]: value
            }
        });
    };

    return (
        <div className="space-y-4 w-full p-4">
            <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                    id="name"
                    value={selectedItem.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                    id="description"
                    value={selectedItem.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select value={selectedItem.type} onValueChange={(value) => handleInputChange('type', value)}>
                    <SelectTrigger id="type">
                        <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="table">Table</SelectItem>
                        <SelectItem value="form">Form</SelectItem>
                        <SelectItem value="chart">Chart</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
                <Label htmlFor="color">Color</Label>
                <Select value={selectedItem.style.color} onValueChange={(value) => handleStyleChange('color', value)}>
                    <SelectTrigger id="color">
                        <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="primary">Primary</SelectItem>
                        <SelectItem value="secondary">Secondary</SelectItem>
                        <SelectItem value="accent">Accent</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
                <Label htmlFor="size">Size</Label>
                <Select value={selectedItem.style.size} onValueChange={(value) => handleStyleChange('size', value)}>
                    <SelectTrigger id="size">
                        <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="sm">Small</SelectItem>
                        <SelectItem value="base">Base</SelectItem>
                        <SelectItem value="lg">Large</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
                <Label htmlFor="font">Font</Label>
                <Select value={selectedItem.style.font} onValueChange={(value) => handleStyleChange('font', value)}>
                    <SelectTrigger id="font">
                        <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="sans">Sans</SelectItem>
                        <SelectItem value="serif">Serif</SelectItem>
                        <SelectItem value="mono">Mono</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Button
                className="w-full bg-burgundy text-burgundy-foreground hover:bg-burgundy/90"
                onClick={handleSaveChanges}
            >
                Save Changes
            </Button>
        </div>
    );
}
